import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ordersAPI } from '../../services/api';
import { printStyles, CompanyLetterhead } from './shared';
import { OrderItemsTablePrint } from './OrderItemsTablePrint';

export function OrderPrintView() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);
  const containerRef = React.useRef(null);

  // Check if opened in preview mode (inside modal iframe)
  const isPreviewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await ordersAPI.get(id);
        setOrder(res.data);
      } catch (err) {
        setError('Failed to load order');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Separate useEffect for print - only triggers when data is ready and NOT in preview mode
  useEffect(() => {
    if (!loading && order && !printTriggered && !isPreviewMode) {
      setPrintTriggered(true);
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, order, printTriggered, isPreviewMode]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount, curr = 'INR') => {
    if (amount === undefined || amount === null) return '-';
    const symbol = curr === 'INR' ? '₹' : curr === 'USD' ? '$' : curr === 'EUR' ? '€' : curr;
    return `${symbol}${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatCurrency4 = (amount, curr = 'INR') => {
    if (amount === undefined || amount === null) return '-';
    const symbol = curr === 'INR' ? '₹' : curr === 'USD' ? '$' : curr === 'EUR' ? '€' : curr;
    return `${symbol}${amount.toLocaleString('en-IN', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <style>{printStyles}</style>
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="print-container">
        <style>{printStyles}</style>
        <p style={{ color: 'red', textAlign: 'center' }}>{error || 'Order not found'}</p>
      </div>
    );
  }

  const currency = order.currency || 'INR';

  return (
    <>
      <div className="print-container landscape" ref={containerRef}>
        <style>{printStyles}</style>
        <style>{`
          .print-container.landscape .info-grid { margin-bottom: 10px; gap: 12px; }
          .print-container.landscape .info-box { padding: 8px 10px; }
          .print-container.landscape .info-box p { margin: 2px 0; font-size: 12px; }
          .print-container.landscape .info-box h4 { margin: 0 0 5px 0; }
          .print-container.landscape .items-table { margin-bottom: 12px; }
          .print-container.landscape .totals-section { margin-bottom: 10px; }
          .so-terms { margin-bottom: 12px; }
          .so-terms .terms-line { font-size: 11px; margin-bottom: 4px; }
          .so-terms .terms-section { margin-top: 8px; }
          .so-signatures { display: flex; gap: 60px; margin-top: 8px; padding-top: 4px; }
          .sig-box { flex: 1; text-align: center; }
          .sig-space { height: 48px; }
          .sig-line { border-top: 1.5px solid #334155; padding-top: 5px; font-size: 11px; font-weight: 700; color: #1e293b; }
          .sig-sub { font-size: 9.5px; color: #64748b; margin-top: 2px; }
          @media print {
            /* Standard A4 landscape margins — signature panel is NOT a
               running footer any more. It's a normal flow element after
               T&C, so it appears once at the very end of the document
               (matches how the browser's own "Print → Save as PDF" lays
               it out; that's the layout the user expects). Reserving a
               per-page margin for it starved intermediate pages of table
               rows (iter 82 regression the user reported). */
            @page { size: A4 landscape; margin: 8mm; }
            .print-container.landscape { padding-bottom: 0; }
            /* Signature panel: stays together, tries to hug the preceding
               T&C so a spare blank line doesn't push it to a fresh page.
               break-before:avoid-page keeps the T&C tail + signatures on
               the same last page whenever there's room. */
            .so-signatures {
              position: static;
              margin-top: 16px;
              padding-top: 8px;
              border-top: 1px solid #e2e8f0;
              page-break-inside: avoid;
              break-inside: avoid;
              break-before: avoid-page;
            }
            .so-signatures .sig-space { height: 42px; }
            .so-sign-spacer { display: none; }
            /* Order-specific line-item T&C paragraphs (order uses
               .so-terms .terms-section p, not the shared .terms-section p). */
            .so-terms .terms-line,
            .so-terms .terms-section h4 { page-break-inside: avoid; }
            .so-terms .terms-section p {
              page-break-inside: avoid;
              orphans: 3;
              widows: 3;
            }
          }
        `}</style>
        
        {/* Print / Save as PDF Button - Hidden from print */}
        {!isPreviewMode && (
          <button 
            className="download-pdf-btn no-print" 
            onClick={() => window.print()}
            data-testid="order-preview-pdf-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Print / Save as PDF
          </button>
        )}
        
        {/* Professional Letterhead */}
        <CompanyLetterhead 
          documentType="ORDER ACCEPTANCE"
          documentNumber={order.order_number}
          revisionNumber={0}
        />

        {/* Customer & Order Info */}
        <div className="info-grid">
          <div className="info-box">
            <h4>Customer Details</h4>
            <p><span className="value">{order.customer_name}</span></p>
            <p>Code: {order.customer_code}</p>
            {order.customer_address && <p>{order.customer_address}</p>}
            {order.customer_gst && <p>GST: {order.customer_gst}</p>}
            {order.customer_type === 'export' && <p style={{ color: '#c2410c' }}>Export Customer</p>}
          </div>
          <div className="info-box">
            <h4>Order Details</h4>
            <p>Date: <span className="value">{formatDate(order.created_at)}</span></p>
            {order.customer_po_number && <p>Customer PO: <span className="value">{order.customer_po_number}</span></p>}
            {order.customer_po_date && <p>PO Date: <span className="value">{formatDate(order.customer_po_date)}</span></p>}
            <p>Quotation Ref: <span className="value">{order.quotation_number}</span></p>
            <p>Currency: <span className="value">{currency}</span>
              {currency !== 'INR' && ` (Rate: ${order.exchange_rate})`}
            </p>
            {order.delivery_terms && <p>Delivery: <span className="value">{order.delivery_terms}</span></p>}
          </div>
        </div>

        {/* Ship-To Address — shown only when explicitly set, distinct from billing */}
        {order.ship_to_address && (
          <div
            className="info-box"
            style={{ marginBottom: '12px', borderLeft: '3px solid #0f766e', paddingLeft: '10px' }}
          >
            <h4 style={{ color: '#0f766e' }}>Ship To</h4>
            <p style={{ whiteSpace: 'pre-wrap' }}>{order.ship_to_address}</p>
          </div>
        )}

        {/* Inquiry Reference */}
        {(order.inquiry_number || order.inquiry_date) && (
          <div style={{ marginBottom: '10px', fontSize: '12px', color: '#475569' }}>
            <strong>Ref:</strong> Inquiry {order.inquiry_number ? `#${order.inquiry_number}` : ''}
            {order.inquiry_date && ` dated ${formatDate(order.inquiry_date)}`}
          </div>
        )}

        {/* Items Table — Dynamic Columns */}
        <OrderItemsTablePrint
          order={order}
          currency={currency}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          formatCurrency4={formatCurrency4}
        />

      {/* Totals — right aligned */}
      <div className="totals-section">
        <div className="totals-box">
          <div className="totals-row">
            <span>Subtotal:</span>
            <span>{formatCurrency(order.subtotal, currency)}</span>
          </div>
          {(order.tooling_total || 0) > 0 && (
            <div className="totals-row">
              <span>Tooling Cost:</span>
              <span>{formatCurrency(order.tooling_total, currency)}</span>
            </div>
          )}
          {(order.sample_total || 0) > 0 && (
            <div className="totals-row">
              <span>Sample Cost:</span>
              <span>{formatCurrency(order.sample_total, currency)}</span>
            </div>
          )}
          {(order.handling_charge || 0) > 0 && (
            <div className="totals-row">
              <span>Handling Charge:</span>
              <span>{formatCurrency(order.handling_charge, currency)}</span>
            </div>
          )}
          {(order.tax_percentage || 0) > 0 && (
            <div className="totals-row">
              <span>Tax ({order.tax_percentage}%):</span>
              <span>{formatCurrency(order.tax_amount, currency)}</span>
            </div>
          )}
          <div className="totals-row total">
            <span>TOTAL AMOUNT:</span>
            <span>{formatCurrency(order.total_amount, currency)}</span>
          </div>
        </div>
      </div>

      {/* Terms — full width, below the totals */}
      <div className="so-terms">
        {order.payment_terms && (
          <div className="terms-line"><strong>Payment Terms:</strong> {order.payment_terms}</div>
        )}
        {order.delivery_terms && (
          <div className="terms-line"><strong>Delivery Terms:</strong> {order.delivery_terms}</div>
        )}
        {order.terms_conditions && (
          <div className="terms-section">
            <h4>Terms & Conditions</h4>
            {order.terms_conditions.split('\n').map((line, i) => (
              <p key={`line-${i}`}>{line}</p>
            ))}
          </div>
        )}
      </div>

      {/* Signature panels — repeated at the bottom of every printed page
          via the reserved @page margin (see @media print block above). */}
      <div className="so-signatures">
        <div className="sig-box">
          <div className="sig-space" />
          <div className="sig-line">For Singhania International Limited</div>
          <div className="sig-sub">Authorised Signatory</div>
        </div>
        <div className="sig-box">
          <div className="sig-space" />
          <div className="sig-line">Customer Acceptance</div>
          <div className="sig-sub">Sign & Stamp / Name / Date</div>
        </div>
      </div>

      {/* Footer */}
      <div className="footer">
        <p>For queries, contact us at info@singhaniainternational.com</p>
        <p>Generated on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      </div>
    </div>
    </>
  );
}


// ============== WORK ORDER PRINT VIEW (TRAVELER) ==============

